import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import api from '../../services/api';
import { Sezione } from './SezioneProcedura';
import { Campo, chiave, annunciaSchedaSalvata, togliRipetizione } from './CampiScheda';
import BottoneRefertoDaScheda from './BottoneRefertoDaScheda';
import RiquadroGravidanze, { dopoQualeSezione } from './RiquadroGravidanze';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import '../../styles/SchedeProcedura.css';

/**
 * Le schede cliniche del medico, compilate dentro una procedura.
 *
 * COSA DISEGNA
 * Una scheda per riquadro - chiuso, come tutti gli altri riquadri della
 * procedura: si apre quello che si compila. Dentro, le sezioni della
 * scheda, e dentro quelle i campi.
 * Le sezioni ripetibili - «Prodotti somministrati» della IV Therapy - si
 * ripetono una volta per prodotto, e ogni ripetizione porta i propri
 * campi: e' cio' che tiene insieme «NAD» con «500 mg».
 *
 * DUE COSE CHE SI VEDONO SOLO QUI
 * 1. Un valore fuori dall'intervallo di riferimento si segnala, non si
 *    rifiuta: una pressione di 160 e' un dato vero e va salvata.
 *    Rifiutarla insegnerebbe a scrivere numeri finti.
 * 2. Le controindicazioni di un prodotto compaiono quando quel prodotto
 *    viene scelto. Sono scritte nel catalogo, accanto alla risposta: il
 *    gestionale le mostra, non le inventa.
 */

const SchedeProcedura = ({ visita }) => {
    const [schede, setSchede] = useState([]);
    const [valori, setValori] = useState({});
    const [righe, setRighe] = useState({});
    /* «Modificato» = diverso dall'istantanea presa al caricamento e a ogni
       salvataggio riuscito, che ricarica (utils/uscitaProtetta.js). */
    const [versione, setVersione] = useState(0);
    const sporco = useSporco({ valori, righe }, versione);
    useUscitaProtetta(sporco);
    // scheda_id -> id della compilazione di questa visita, se gia' salvata
    const [compilazioni, setCompilazioni] = useState({});
    const [scrivibile, setScrivibile] = useState(false);
    const [nascosta, setNascosta] = useState(false);
    const [salvando, setSalvando] = useState(null);
    const [errore, setErrore] = useState(null);
    const [salvata, setSalvata] = useState(null);
    /* Una scheda NUOVA si compila solo il giorno della procedura (niente di
       retroattivo, decisione di Alex del 23 settembre 2026); quelle gia'
       compilate si risalvano sempre. Lo dice il server, che lo pretende. */
    const [siCompilaOggi, setSiCompilaOggi] = useState(false);

    const visitaId = visita?.id;

    const carica = useCallback(async () => {
        if (!visitaId) return;
        try {
            const r = await api.get(`/schede/compilazione/${visitaId}`);
            const d = r.data?.data || {};
            setSchede(d.schede || []);
            setValori(d.valori || {});
            /* Per SEZIONE: con un conto per scheda ogni sezione ripetibile
               disegnava le righe delle altre. */
            setRighe(d.righe_sezioni || {});
            setVersione((v) => v + 1);
            setCompilazioni(d.compilazioni || {});
            setScrivibile(d.scrivibile === true);
            setSiCompilaOggi(d.si_compila_oggi === true);
            setNascosta(d.riservata_nascosta === true);
            setErrore(null);
        } catch (e) {
            setErrore('Non riesco a leggere le schede');
        }
    }, [visitaId]);

    useEffect(() => { carica(); }, [carica]);

    const quanteRighe = useCallback((sez) => Math.max(1, righe[sez.id] || 1), [righe]);

    const cambia = (campoId, riga, v) => {
        setValori((precedenti) => ({ ...precedenti, [chiave(campoId, riga)]: v }));
        setSalvata(null);
    };

    const salva = async (scheda) => {
        setSalvando(scheda.id);
        setErrore(null);
        try {
            const voci = [];
            for (const sez of scheda.sezioni) {
                const n = sez.ripetibile ? quanteRighe(sez) : 1;
                for (let riga = 1; riga <= n; riga++) {
                    for (const c of sez.campi) {
                        const k = chiave(c.campo_id, riga);
                        if (!(k in valori)) continue;
                        voci.push({ campo_id: c.campo_id, riga, valore: valori[k] });
                    }
                }
            }
            if (!voci.length) { setSalvando(null); return; }
            await api.put(`/schede/compilazione/${visitaId}/${scheda.id}`, { valori: voci });
            annunciaSchedaSalvata();
            setSalvata(scheda.id);
            await carica();
        } catch (e) {
            setErrore(e?.message || 'Salvataggio non riuscito');
        } finally {
            setSalvando(null);
        }
    };

    const aggiungiRiga = (sez) =>
        setRighe((p) => ({ ...p, [sez.id]: quanteRighe(sez) + 1 }));

    /* Toglie UNA ripetizione di UNA sezione: il server tocca solo i campi
       di quella sezione e fa scalare le righe dopo. A schermo la stessa
       cosa, senza ricaricare: quello che e' scritto e non ancora salvato
       nelle altre righe e nelle altre schede resta dov'e'. */
    const togliRiga = async (scheda, sez, riga) => {
        if (!window.confirm(`Togliere «${sez.ripetibile_etichetta} ${riga}»? `
                          + 'Le altre risposte della scheda restano.')) return;
        const eraPulita = !sporco;
        try {
            await api.delete(`/schede/compilazione/${visitaId}/${scheda.id}/riga/${riga}`,
                             { params: { sezione: sez.id } });
            setValori((p) => togliRipetizione(p, sez, riga));
            setRighe((p) => ({ ...p, [sez.id]: Math.max(1, quanteRighe(sez) - 1) }));
            if (eraPulita) setVersione((v) => v + 1);
        } catch (e) {
            setErrore(e?.message || 'Non riesco a togliere la ripetizione');
        }
    };

    const vuoto = useMemo(() => !schede.length, [schede]);

    /* Si scrive in una scheda se si vede la procedura e, per una scheda
       che non e' ancora stata compilata, se la procedura e' di oggi. */
    const scriviQui = (s) => scrivibile && (!!compilazioni[s.id] || siCompilaOggi);
    const giornoProcedura = String(visita?.data_visita || '').slice(0, 10).split('-').reverse().join('/');

    if (nascosta) return null;
    if (vuoto) return null;

    return (
        <>
            {errore && <p className="sp-errore">{errore}</p>}
            {schede.map((s) => (
                <Sezione key={s.id} title={s.nome}
                         right={s.area_nome ? <span className="sp-area">{s.area_nome}</span> : null}>
                    {/* Una scheda senza nessun campo e' un segnaposto: il
                        riquadro c'e' perche' si sappia che quella cosa va
                        registrata, ma i campi non sono stati ancora decisi.
                        Dirlo in chiaro evita che qualcuno la scambi per una
                        scheda rotta e la segnali come guasto. */}
                    {s.sezioni.every((z) => !z.campi.length) && (
                        <p className="sp-segnaposto">
                            Questa scheda non ha ancora campi: il riquadro è pronto,
                            il contenuto si compone dal <b>catalogo dei campi</b>.
                        </p>
                    )}

                    {s.sezioni.filter((z) => z.campi.length > 0).map((sez) => (
                      <React.Fragment key={sez.id}>
                        <div className="sp-sezione">
                            <div className="sp-sezione-titolo">{sez.titolo}</div>

                            {sez.ripetibile ? (
                                <>
                                    {Array.from({ length: quanteRighe(sez) }, (_, i) => i + 1).map((riga) => (
                                        <div className="sp-ripetizione" key={riga}>
                                            <div className="sp-ripetizione-testa">
                                                <span>{sez.ripetibile_etichetta} {riga}</span>
                                                {scriviQui(s) && quanteRighe(sez) > 1 && (
                                                    <button type="button" onClick={() => togliRiga(s, sez, riga)}
                                                            title="Togli questa ripetizione">
                                                        <Trash2 size={13} />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="sp-campi">
                                                {sez.campi.map((c) => (
                                                    <Campo key={c.campo_id} campo={c} riga={riga}
                                                           valore={valori[chiave(c.campo_id, riga)]}
                                                           bloccato={!scriviQui(s)}
                                                           onChange={(v) => cambia(c.campo_id, riga, v)} />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    {scriviQui(s) && (
                                        <button type="button" className="sp-aggiungi"
                                                onClick={() => aggiungiRiga(sez)}>
                                            <Plus size={13} aria-hidden="true" /> Aggiungi {sez.ripetibile_etichetta?.toLowerCase()}
                                        </button>
                                    )}
                                </>
                            ) : (
                                <div className="sp-campi">
                                    {sez.campi.map((c) => (
                                        <Campo key={c.campo_id} campo={c} riga={1}
                                               valore={valori[chiave(c.campo_id, 1)]}
                                               bloccato={!scriviQui(s)}
                                               onChange={(v) => cambia(c.campo_id, 1, v)} />
                                    ))}
                                </div>
                            )}
                        </div>
                        {/* Gravidanze, climaterio e menopausa: il riquadro
                            dell'anamnesi, lo stesso dato (RiquadroGravidanze).
                            Oggi la scheda ginecologica sta nel profilo; se la
                            si sposta qui, il riquadro la segue. */}
                        {dopoQualeSezione(s.codice, s.sezioni) === sez.id && (
                            <RiquadroGravidanze pazienteId={visita.paziente_id} origine="scheda_ginecologica"
                                visitaId={visita.id} compilazioneId={compilazioni[s.id] || null} />
                        )}
                      </React.Fragment>
                    ))}
                    {dopoQualeSezione(s.codice, s.sezioni) === 'fondo' && (
                        <RiquadroGravidanze pazienteId={visita.paziente_id} origine="scheda_ginecologica"
                            visitaId={visita.id} compilazioneId={compilazioni[s.id] || null} />
                    )}

                    {scrivibile && !scriviQui(s) && s.sezioni.some((z) => z.campi.length > 0) && (
                        <p className="sp-segnaposto">
                            Questa scheda non è stata compilata il giorno della procedura, il {giornoProcedura}:
                            una scheda si compila il giorno della procedura. Niente di retroattivo.
                        </p>
                    )}
                    {scriviQui(s) && s.sezioni.some((z) => z.campi.length > 0) && (
                        <div className="sp-azioni">
                            {salvata === s.id && <span className="sp-salvata">Salvata</span>}
                            {/* Solo dopo il primo salvataggio: prima la
                                compilazione non esiste e non c'e' niente da copiare. */}
                            <BottoneRefertoDaScheda compilazioneId={compilazioni[s.id]} disabilitato={salvando === s.id} />
                            <button type="button" onClick={() => salva(s)} disabled={salvando === s.id}>
                                {salvando === s.id ? 'Salvo…' : 'Salva la scheda'}
                            </button>
                        </div>
                    )}
                </Sezione>
            ))}
        </>
    );
};

export default SchedeProcedura;
